/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { roleService } from '../../../../ServiceFactory';
import { connect } from 'react-redux';
import { RoleKey, RoleManagementPermission } from '../../../../shared/services/Role.service';
import { PlatformAdminUIState } from '../../../../store/store';
import ActionMenuView from '../../../../shared/views/ActionMenu/ActionMenu.view';
import { ActionMenuItemProps } from '../../../../shared/views/ActionMenu/ActionMenu';
import RejectFlagActionContainer from '../actions/RejectFlagAction.container';
import AcceptFlagActionContainer from '../actions/AcceptFlagAction.container';
import {
	ClientUserGeneratedContentFlagChatStatusEnum,
	ClientUserGeneratedContentFlagCommentStatusEnum,
	ClientUserGeneratedContentFlagDetailStatusEnum,
	ClientUserGeneratedContentFlagGroupStatusEnum,
	ClientUserGeneratedContentFlagPostStatusEnum,
	ListFlagsIncludedStatusEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';

interface Props {
	roleManagementPermission: RoleManagementPermission;
	flagStatus:
		| ClientUserGeneratedContentFlagDetailStatusEnum
		| ClientUserGeneratedContentFlagChatStatusEnum
		| ClientUserGeneratedContentFlagCommentStatusEnum
		| ClientUserGeneratedContentFlagGroupStatusEnum
		| ClientUserGeneratedContentFlagPostStatusEnum
		| undefined;
}

function getValidStatusChangeActionsByStatus(
	props: ActionMenuItemProps,
	flagStatus:
		| ClientUserGeneratedContentFlagDetailStatusEnum
		| ClientUserGeneratedContentFlagChatStatusEnum
		| ClientUserGeneratedContentFlagCommentStatusEnum
		| ClientUserGeneratedContentFlagGroupStatusEnum
		| ClientUserGeneratedContentFlagPostStatusEnum
): JSX.Element[] {
	switch (flagStatus.toString()) {
		case ListFlagsIncludedStatusEnum.Open:
			return [
				<AcceptFlagActionContainer key="acceptFlagAction" {...props} />,
				<RejectFlagActionContainer key="rejectFlagAction" {...props} />
			];
		case ListFlagsIncludedStatusEnum.InProgress:
			return [
				<AcceptFlagActionContainer key="acceptFlagAction" {...props} />,
				<RejectFlagActionContainer key="rejectFlagAction" {...props} />
			];
		default:
			return [];
	}
}

class FlaggedDetailsActionsMenuContainer extends Component<Props> {
	render(): JSX.Element | null {
		const { roleManagementPermission, flagStatus } = this.props;
		const validRoles: RoleKey[] = [RoleKey.GLOBAL_USER_GENERATED_CONTENT_ADMIN, RoleKey.USER_GENERATED_CONTENT_ADMIN];

		if (
			!roleService().hasClientRoleAssignmentSomeValidRole(roleManagementPermission.roles, validRoles) ||
			!flagStatus ||
			flagStatus.toString() === ListFlagsIncludedStatusEnum.Accepted ||
			flagStatus.toString() === ListFlagsIncludedStatusEnum.Rejected
		) {
			return null;
		}

		return (
			<ActionMenuView
				renderItems={(props: ActionMenuItemProps): JSX.Element[] =>
					getValidStatusChangeActionsByStatus(props, flagStatus)
				}
			/>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		roleManagementPermission: state.global.roleManagementPermission
	};
};

export default connect(mapStateToProps)(FlaggedDetailsActionsMenuContainer);
